/* global debounce */

/**
 * =========================================================
 * component-facets.js - 集合页筛选和排序功能核心逻辑
 * =========================================================
 *
 * 文件关联说明：
 * - 本文件被 sections/main-collection-product-grid.liquid 引入（第187行）
 * - 操作 snippets/component-facets.liquid 渲染的 DOM 结构
 * - 主要功能：自定义排序下拉、筛选计数、AJAX 无刷新更新产品列表
 *
 * 核心功能：
 * 1. 自定义排序下拉组件（替换原生 select，保留原生功能）
 * 2. 活动筛选条件计数（基于 .active-facets-desktop-js 容器）
 * 3. 筛选表单 AJAX 提交和页面局部更新
 * 4. 兼容 Dawn 主题的 AJAX 重绘机制（DOM 替换后重新绑定事件）
 *
 * 关键实现策略：
 * - initComponentFacetsSort()：初始化自定义排序下拉，支持重复调用
 * - FacetFiltersForm：自定义元素类，处理筛选表单的提交和渲染
 * - MutationObserver：监听 DOM 变化，自动重新初始化排序组件
 * - customElements.define：注册自定义元素，防止重复注册
 */

/**
 * =========================================================
 * 自定义排序下拉组件初始化函数
 * =========================================================
 *
 * 功能说明：
 * 将原生的 select 下拉框替换为自定义的按钮+面板 UI，同时保留原生 select 的功能
 * 用于与 Shopify Dawn 主题的 AJAX 筛选系统集成
 *
 * DOM 结构约定（对应 component-facets.liquid 中的结构）：
 * - 根容器： [data-sort-select]
 * - 原生 select（隐藏但保留）： select[name="sort_by"]
 * - 触发按钮： .component-facets__sort-trigger-js
 * - 显示文案： [data-sort-label]
 * - 下拉面板： [data-sort-panel]
 * - 选项按钮： .component-facets__sort-option-js[data-sort-value]
 *
 * 交互行为：
 * - 点击触发按钮：打开/关闭下拉面板
 * - 点击选项：更新 select 值 → 触发 change/input 事件 → 关闭面板 → 触发 AJAX 排序
 * - 点击外部区域：关闭面板
 * - 按 ESC 键：关闭面板
 * - AJAX 重绘后：重新调用此函数即可恢复功能
 *
 * @param {HTMLElement|Document} scope - 搜索范围，默认为 document，可传入特定容器以提高性能
 */
function initComponentFacetsSort(scope = document) {
  // 查找所有需要初始化的排序下拉容器
  const roots = Array.from(scope.querySelectorAll('[data-sort-select]'))
  if (roots.length === 0) return

  roots.forEach(root => {
    // 防止重复绑定事件：如果已经初始化过，只同步 UI 状态
    if (root.dataset.sortInited === '1') {
      // 已初始化的情况下，只同步 label 和 active 状态（防止 AJAX 更新后状态不一致）
      syncFromSelect(root)
      syncActiveUI(root)
      return
    }
    // 标记为已初始化，避免重复绑定事件监听器
    root.dataset.sortInited = '1'

    // 获取所有必需的 DOM 元素
    const select = root.querySelector('select[name="sort_by"]')
    const trigger = root.querySelector('.component-facets__sort-trigger-js')
    const panel = root.querySelector('[data-sort-panel]')
    const labelEl = root.querySelector('[data-sort-label]')
    const options = Array.from(root.querySelectorAll('.component-facets__sort-option-js'))

    // 如果缺少任何必需元素，跳过初始化
    if (!select || !trigger || !panel || !labelEl || options.length === 0) return

    /**
     * 工具函数：从原生 select 同步显示文案到自定义按钮
     * 用于保持自定义 UI 与原生 select 的值一致
     */
    function syncLabelFromSelect() {
      const selected = select.options[select.selectedIndex]
      // 使用可选链安全地访问 textContent
      labelEl.textContent = selected?.textContent?.trim() || ''
    }

    /**
     * 工具函数：同步选项的激活状态
     * 根据当前 select 的值，为对应的选项按钮添加 is-active 类和 aria-selected 属性
     */
    function syncActive() {
      const currentValue = select.value
      options.forEach(btn => {
        const v = btn.getAttribute('data-sort-value')
        const isActive = v === currentValue
        btn.classList.toggle('is-active', isActive)
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false')
      })
    }

    /**
     * 打开下拉面板
     * 使用 class 和 hidden 属性双重控制，确保兼容性
     */
    function open() {
      root.classList.add('is-open')
      panel.hidden = false
      trigger.setAttribute('aria-expanded', 'true')
    }

    /**
     * 关闭下拉面板
     */
    function close() {
      root.classList.remove('is-open')
      trigger.setAttribute('aria-expanded', 'false')
      panel.hidden = true
    }

    /**
     * 切换下拉面板的打开/关闭状态
     */
    function toggle() {
      panel.hidden ? open() : close()
    }

    // 绑定触发按钮的点击事件：切换下拉面板
    trigger.addEventListener('click', e => {
      e.preventDefault()
      toggle()
    })

    // 绑定选项按钮的点击事件：选择排序方式并触发 AJAX 更新
    options.forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault()

        const value = btn.getAttribute('data-sort-value')
        if (!value) return

        // 步骤1：更新原生 select 的值并触发事件（触发 Dawn 的 AJAX 筛选）
        if (select.value !== value) {
          select.value = value
          select.dispatchEvent(new Event('change', { bubbles: true }))
          select.dispatchEvent(new Event('input', { bubbles: true }))
        }

        // 步骤2：立即更新自定义 UI（提供即时视觉反馈）
        syncLabelFromSelect()
        syncActive()

        // 步骤3：关闭下拉面板（用户点击后立即关闭）
        close()
        // 将焦点返回到触发按钮，提升可访问性
        trigger.focus()
      })
    })

    /**
     * 点击外部区域关闭下拉面板
     * 使用 pointerdown 事件而非 click，避免与面板内点击冲突
     */
    const onPointerDown = e => {
      if (!root.contains(e.target)) close()
    }
    document.addEventListener('pointerdown', onPointerDown)

    /**
     * 按 ESC 键关闭下拉面板
     * 仅在当前下拉面板处于打开状态时处理
     */
    const onKeyDown = e => {
      if (e.key !== 'Escape') return
      if (root.classList.contains('is-open')) close()
    }
    document.addEventListener('keydown', onKeyDown)

    /**
     * 清理函数：移除事件监听器
     * 在组件被移除或重新初始化时调用，防止内存泄漏
     */
    root._componentFacetsSortCleanup = () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }

    // 初始化时同步一次 UI 状态
    syncLabelFromSelect()
    syncActive()

    // 暴露 API 供外部调用（可选，用于程序化控制）
    root._componentFacetsSort = { open, close, syncLabelFromSelect, syncActive }
  })

  /**
   * 辅助函数：从 select 同步 label（用于已初始化的组件）
   * @param {HTMLElement} root - 排序下拉的根容器
   */
  function syncFromSelect(root) {
    const select = root.querySelector('select[name="sort_by"]')
    const labelEl = root.querySelector('[data-sort-label]')
    if (!select || !labelEl) return
    const selected = select.options[select.selectedIndex]
    // 使用可选链安全地访问 textContent
    labelEl.textContent = selected?.textContent?.trim() || ''
  }

  /**
   * 辅助函数：同步选项的激活状态（用于已初始化的组件）
   * @param {HTMLElement} root - 排序下拉的根容器
   */
  function syncActiveUI(root) {
    const select = root.querySelector('select[name="sort_by"]')
    const options = Array.from(root.querySelectorAll('.component-facets__sort-option-js'))
    if (!select || options.length === 0) return
    const currentValue = select.value
    options.forEach(btn => {
      const v = btn.getAttribute('data-sort-value')
      const isActive = v === currentValue
      btn.classList.toggle('is-active', isActive)
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false')
    })
  }
}

/**
 * =========================================================
 * FacetFiltersForm - 筛选表单自定义元素类
 * =========================================================
 *
 * 功能说明：
 * 继承自 HTMLElement，实现自定义元素 <facet-filters-form>
 * 负责处理集合页的筛选和排序功能，包括：
 * - 筛选条件的添加/移除
 * - 表单输入的防抖提交
 * - AJAX 无刷新更新产品列表
 * - 活动筛选条件的计数显示
 * - 网格视图状态管理（静态属性 + sessionStorage 持久化）
 *
 * 对应 DOM 结构：
 * 在 component-facets.liquid 中使用 <facet-filters-form> 标签包裹表单
 */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super()

    // 绑定移除筛选条件的点击回调（this 绑定）
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this)

    // 创建防抖提交函数：300ms 延迟，避免频繁请求
    this.debouncedOnSubmit = debounce(event => {
      this.onSubmitHandler(event)
    }, 300)

    // 监听表单输入事件，使用防抖提交
    const facetForm = this.querySelector('form')
    if (facetForm) facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this))
  }

  // =========================
  // 网格视图状态管理
  // =========================

  /**
   * 获取当前网格布局
   * 优先从类静态属性读取，若为空则从 sessionStorage 恢复
   * @returns {string} 网格布局类名（grid-layout-1 或 grid-layout-2）
   */
  static getCurrentGridLayout() {
    if (!FacetFiltersForm.currentGridLayout) {
      // 尝试从 sessionStorage 恢复
      FacetFiltersForm.currentGridLayout = sessionStorage.getItem('shopify-grid-layout') || 'grid-layout-1'
    }
    return FacetFiltersForm.currentGridLayout
  }

  /**
   * 设置当前网格布局
   * 同时保存到类静态属性和 sessionStorage（实现持久化）
   * @param {string} layout - 网格布局类名（grid-layout-1 或 grid-layout-2）
   */
  static setCurrentGridLayout(layout) {
    FacetFiltersForm.currentGridLayout = layout
    sessionStorage.setItem('shopify-grid-layout', layout)
  }

  /**
   * 设置浏览器历史记录监听器
   * 处理浏览器前进/后退按钮，恢复对应的筛选状态
   */
  static setListeners() {
    const onHistoryChange = event => {
      // 从历史记录中获取搜索参数，如果没有则使用初始参数
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial
      // 如果参数未变化，跳过渲染（避免重复请求）
      if (searchParams === FacetFiltersForm.searchParamsPrev) return

      // 渲染对应状态的产品列表（不更新 URL，因为已经在历史记录中）
      FacetFiltersForm.renderPage(searchParams, null, false)

      // 浏览器前进/后退后，DOM 可能被替换，需要重新更新计数和初始化排序 UI
      FacetFiltersForm.safeUpdateActiveFacetCount()
      initComponentFacetsSort()
    }
    window.addEventListener('popstate', onHistoryChange)
  }

  // =========================
  // 活动筛选条件计数功能
  // =========================

  /**
   * 从 DOM 中获取当前活动筛选条件的数量
   * 只统计 .active-facets-desktop-js 容器中的 facet-remove 元素
   * @returns {number|null} 筛选条件数量，如果容器不存在则返回 null
   */
  static getActiveFacetCountFromDOM() {
    const container = document.querySelector('.active-facets-desktop-js')
    if (!container) return null
    // 排除包装器元素，只统计实际的筛选条件
    const activeItems = container.querySelectorAll('facet-remove:not(.active-facets__button-wrapper)')
    return activeItems.length
  }

  /**
   * 更新活动筛选条件的计数显示
   * 同时控制计数徽章和"Clear All"按钮的显示/隐藏
   */
  static updateActiveFacetCount() {
    const countEl = document.getElementById('ActiveFacetCount')
    if (!countEl) return

    const domCount = FacetFiltersForm.getActiveFacetCountFromDOM()
    // 如果容器不存在，不更新（避免覆盖首屏 Liquid 渲染的值，防止闪动）
    if (domCount === null) return

    // 更新计数数字
    countEl.textContent = String(domCount)
    // 当计数为 0 时隐藏徽章
    if (domCount === 0) {
      countEl.style.display = 'none'
    } else {
      countEl.style.display = 'flex'
    }

    // 同步控制"Clear All"按钮的显示/隐藏
    const clearEls = document.querySelectorAll('.component-facets__clear-js')
    clearEls.forEach(el => {
      el.style.display = domCount === 0 ? 'none' : 'flex'
    })
  }

  /**
   * 安全更新计数：使用 requestAnimationFrame 确保 DOM 更新完成后再更新
   * 用于处理 AJAX 更新后 DOM 可能尚未完全渲染的情况
   */
  static safeUpdateActiveFacetCount() {
    FacetFiltersForm.updateActiveFacetCount()
    requestAnimationFrame(() => FacetFiltersForm.updateActiveFacetCount())
  }

  /**
   * 设置计数功能的初始化
   * 在 DOM 加载完成后和页面完全加载后各执行一次
   */
  static setupActiveFacetCountInit() {
    const init = () => FacetFiltersForm.safeUpdateActiveFacetCount()
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true })
    } else {
      init()
    }
    window.addEventListener('load', init, { once: true })
  }

  /**
   * 设置 MutationObserver 监听 DOM 变化
   * 当筛选条件容器、计数元素或排序组件被替换时，自动更新计数和重新初始化排序
   *
   * 性能优化：仅监听筛选器相关的容器，避免全局监听影响 Core Web Vitals（INP）
   */
  static setupActiveFacetCountObserver() {
    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        // 只处理子节点变化
        if (m.type !== 'childList') continue

        // 收集所有新增和移除的节点
        const changedNodes = [...Array.from(m.addedNodes || []), ...Array.from(m.removedNodes || [])]
        // 检查是否有相关元素被修改
        const hit = changedNodes.some(node => {
          if (!(node instanceof Element)) return false
          return (
            node.matches?.('.active-facets-desktop-js, #ActiveFacetCount, .sorting-js, [data-sort-select]') ||
            node.querySelector?.('.active-facets-desktop-js, #ActiveFacetCount, .sorting-js, [data-sort-select]')
          )
        })

        if (hit) {
          // 更新计数
          FacetFiltersForm.safeUpdateActiveFacetCount()
          // 排序组件也可能被替换，一并重新初始化
          initComponentFacetsSort()
          break
        }
      }
    })

    // 【性能优化】严格限制监听范围，避免全局监听导致频繁触发
    // 仅监听筛选器相关容器，找不到则不启用 observer（防止性能问题）
    const targetContainer =
      document.querySelector('.facets-container-js') || // 筛选器专属容器
      document.querySelector('facet-filters-form') // 筛选表单自定义元素

    // 只有找到目标容器时才启用 observer（移除 body/main 降级，避免性能问题）
    if (targetContainer) {
      observer.observe(targetContainer, { childList: true, subtree: true })
      FacetFiltersForm._activeFacetCountObserver = observer
    }
  }

  // =========================
  // AJAX 页面渲染逻辑
  // =========================

  /**
   * 渲染产品列表页面（AJAX 无刷新更新）
   * 这是筛选和排序功能的核心方法，负责更新产品列表、筛选条件、计数等
   *
   * @param {string} searchParams - URL 搜索参数字符串（如 "sort_by=price-ascending&filter.v.availability=1"）
   * @param {Event} event - 触发渲染的事件对象（可选）
   * @param {boolean} updateURLHash - 是否更新浏览器 URL（默认 true）
   */
  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams
    const sections = FacetFiltersForm.getSections()
    const countContainer = document.getElementById('ProductCount')
    const countContainerDesktop = document.getElementById('ProductCountDesktop')
    const loadingSpinners = document.querySelectorAll(
      '.facets-container-js .loading__spinner, facet-filters-form .loading__spinner'
    )

    loadingSpinners.forEach(spinner => spinner.classList.remove('hidden'))

    const gridContainer = document.getElementById('ProductGridContainer')
    // 使用可选链简化 DOM 访问
    gridContainer?.querySelector('.collection')?.classList.add('loading')

    countContainer?.classList.add('loading')
    countContainerDesktop?.classList.add('loading')

    sections.forEach(section => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`
      const filterDataUrl = element => element.url === url

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event)
    })

    // 更新浏览器 URL（如果允许）
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams)

    // 渲染完成后，确保计数和排序 UI 状态正确
    FacetFiltersForm.safeUpdateActiveFacetCount()
    initComponentFacetsSort()
  }

  /**
   * 从服务器获取并渲染页面片段
   * 当缓存中不存在对应 URL 的数据时调用
   *
   * @param {string} url - 要获取的 URL
   * @param {Event} event - 触发渲染的事件对象
   */
  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.text()
      })
      .then(responseText => {
        const html = responseText

        // 校验响应包含有效的筛选内容，防止错误页面污染缓存和清空筛选项
        const parsed = new DOMParser().parseFromString(html, 'text/html')
        const hasFilters = parsed.querySelector('#FacetFiltersFormMobile .js-filter')
        const hasGrid = parsed.getElementById('ProductGridContainer')
        if (!hasFilters && !hasGrid) return

        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }]
        FacetFiltersForm.renderFilters(html, event)
        FacetFiltersForm.renderProductGridContainer(html)
        FacetFiltersForm.renderProductCount(html)

        // 如果存在滚动动画初始化函数，调用它（用于产品卡片的进入动画）
        if (typeof window.initializeScrollAnimationTrigger === 'function') {
          const gridContainer = parsed.getElementById('ProductGridContainer')
          if (gridContainer) {
            window.initializeScrollAnimationTrigger(gridContainer.innerHTML)
          }
        }

        // fetch 完成后再次更新计数和排序 UI（避免异步替换导致的延迟）
        FacetFiltersForm.safeUpdateActiveFacetCount()
        initComponentFacetsSort()
      })
      .catch(() => {
        // 请求失败时隐藏 loading 状态，不修改筛选项 DOM
        const loadingSpinners = document.querySelectorAll(
          '.facets-container-js .loading__spinner, facet-filters-form .loading__spinner'
        )
        loadingSpinners.forEach(spinner => spinner?.classList.add('hidden'))
        document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.remove('loading')
      })
  }

  /**
   * 从缓存中渲染页面片段
   * 当之前已经获取过对应 URL 的数据时，直接从缓存读取
   *
   * @param {Function} filterDataUrl - 用于查找缓存数据的过滤函数
   * @param {Event} event - 触发渲染的事件对象
   */
  static renderSectionFromCache(filterDataUrl, event) {
    const { html } = FacetFiltersForm.filterData.find(filterDataUrl)
    FacetFiltersForm.renderFilters(html, event)
    FacetFiltersForm.renderProductGridContainer(html)
    FacetFiltersForm.renderProductCount(html)

    // 初始化滚动动画
    if (typeof window.initializeScrollAnimationTrigger === 'function') {
      const parsed = new DOMParser().parseFromString(html, 'text/html')
      const gridContainer = parsed.getElementById('ProductGridContainer')
      if (gridContainer) {
        window.initializeScrollAnimationTrigger(gridContainer.innerHTML)
      }
    }

    // 更新计数和排序 UI
    FacetFiltersForm.safeUpdateActiveFacetCount()
    initComponentFacetsSort()
  }

  /**
   * 渲染产品网格容器
   * 替换 #ProductGridContainer 的内容，并取消滚动动画（避免重复触发）
   *
   * @param {string} html - 包含新内容的 HTML 字符串
   */
  static renderProductGridContainer(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html')
    const newGrid = parsed.getElementById('ProductGridContainer')
    const gridContainer = document.getElementById('ProductGridContainer')
    if (!newGrid || !gridContainer) return

    gridContainer.innerHTML = newGrid.innerHTML
    // 取消所有滚动触发器的动画（避免新内容立即触发动画）
    gridContainer.querySelectorAll('.scroll-trigger').forEach(el => el.classList.add('scroll-trigger--cancel'))

    // 恢复网格视图状态（筛选/排序后保持用户选择的视图）
    const currentLayout = FacetFiltersForm.getCurrentGridLayout()
    if (currentLayout) {
      const productGrid = gridContainer.querySelector('.wd-collection__grid')
      if (productGrid) {
        productGrid.classList.remove('grid-layout-1', 'grid-layout-2')
        productGrid.classList.add(currentLayout)
      }

      // 同步按钮激活状态和 aria-pressed
      const gridNumber = currentLayout.split('-')[2] // 提取 1 或 2
      const toggleBtns = document.querySelectorAll('.wd-grid-toggle__btn')
      toggleBtns.forEach(btn => {
        const btnType = btn.getAttribute('data-grid-type')
        const isActive = btnType === `grid-${gridNumber}`
        btn.classList.toggle('active', isActive)
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      })
    }

    // 重新初始化 Trustpilot widgets（AJAX 更新后需要重新加载）
    FacetFiltersForm.reinitializeTrustpilot()

    // 通知集合页对比等依赖产品网格的功能重新同步
    try {
      document.dispatchEvent(new CustomEvent('collection:grid:updated'))
    } catch {
      // 不支持自定义事件时不影响筛选结果渲染
    }
  }

  /**
   * 重新初始化 Reviews.io 评分组件
   * 在 AJAX 更新产品网格后调用，确保评分 snippet 正常显示
   *
   * Reviews.io dist.js 内部用 ratingSnippetSkuMap 去重：首次调用后
   * 同一批 SKU 会被跳过。需手动清空该 map 才能触发重新请求。
   */
  static reinitializeTrustpilot() {
    if (typeof window.ratingSnippet === 'undefined') {
      return
    }

    // 重置 Reviews.io 内部 SKU 缓存，否则 AJAX 后相同产品不会重新请求评分
    window.ratingSnippetSkuMap = []
    window.ratingSnippetBatchNum = 0

    try {
      window.ratingSnippet('ruk_rating_snippet', {
        store: 'www.waterdropfilter.ca',
        color: '#ffd200',
        linebreak: true,
        usePolaris: true,
        text: 'Reviews'
      })
    } catch (error) {
      console.warn('Reviews.io snippet reload failed:', error)
    }
  }

  /**
   * 渲染产品数量显示
   * 更新产品计数文本并隐藏加载动画
   *
   * @param {string} html - 包含新内容的 HTML 字符串
   */
  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html')
    const newCountEl = parsed.getElementById('ProductCount')
    const container = document.getElementById('ProductCount')
    const containerDesktop = document.getElementById('ProductCountDesktop')

    if (newCountEl && container) {
      container.innerHTML = newCountEl.innerHTML
      container.classList.remove('loading')
    }
    if (newCountEl && containerDesktop) {
      containerDesktop.innerHTML = newCountEl.innerHTML
      containerDesktop.classList.remove('loading')
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container-js .loading__spinner, facet-filters-form .loading__spinner'
    )
    // 隐藏所有加载动画（使用可选链确保安全）
    loadingSpinners.forEach(spinner => spinner?.classList.add('hidden'))
  }

  /**
   * 渲染筛选条件
   * 更新筛选器列表、活动筛选条件、排序组件等
   *
   * @param {string} html - 包含新内容的 HTML 字符串
   * @param {Event} event - 触发渲染的事件对象
   */
  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    )
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    )

    Array.from(facetDetailsElementsFromDom).forEach(currentElement => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove()
      }
    })

    const matchesId = element => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined
      return jsFilter ? element.id === jsFilter.id : false
    }

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(element => !matchesId(element))
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId)

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id)

      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1]
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId)?.after(elementToRender)
            return
          }
        }

        if (elementToRender.parentElement?.id) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`)?.before(elementToRender)
        }
      }
    })

    // 渲染活动筛选条件（已选中的筛选标签）
    FacetFiltersForm.renderActiveFacets(parsedHTML)
    // 渲染其他元素（如排序组件、移动端计数等）
    FacetFiltersForm.renderAdditionalElements(parsedHTML)

    if (countsToRender && event?.target) {
      // 使用可选链简化访问
      const closest = event.target.closest('.js-filter')
      const closestJSFilterID = closest?.id

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, closest)
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID))

        const newFacetDetailsElement = document.getElementById(closestJSFilterID)
        const newElementSelector = newFacetDetailsElement?.classList.contains('mobile-facets__details-js')
          ? `.mobile-facets__close-button`
          : `.facets__summary`
        const newElementToActivate = newFacetDetailsElement?.querySelector(newElementSelector)

        const isTextInput = event.target.getAttribute('type') === 'text'
        if (newElementToActivate && !isTextInput) newElementToActivate?.focus()
      }
    }
  }

  /**
   * 渲染活动筛选条件
   * 更新显示已选中筛选条件的容器内容
   *
   * @param {Document} html - 解析后的 HTML 文档对象
   */
  static renderActiveFacets(html) {
    const selector = '.active-facets-desktop-js'
    const activeFacetsElement = html.querySelector(selector)
    if (activeFacetsElement) {
      const target = document.querySelector(selector)
      if (target) target.innerHTML = activeFacetsElement.innerHTML
    }

    // 更新筛选条件计数
    FacetFiltersForm.safeUpdateActiveFacetCount()
  }

  /**
   * 渲染其他附加元素
   * 包括移动端计数、排序组件等
   * 关键：排序组件 DOM 被替换后，立即重新初始化自定义排序功能
   *
   * @param {Document} html - 解析后的 HTML 文档对象
   */
  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__count-js', '.sorting-js']

    mobileElementSelectors.forEach(selector => {
      const sourceElement = html.querySelector(selector)
      if (!sourceElement) return
      const target = document.querySelector(selector)
      // 使用可选链安全地访问 innerHTML
      if (target) target.innerHTML = sourceElement.innerHTML
    })

    // 关键步骤：排序组件 DOM 替换完成后，重新初始化自定义排序功能
    // 使用 .sorting-js 容器作为搜索范围，提高性能
    const sortingScope = document.querySelector('.sorting-js') || document
    initComponentFacetsSort(sortingScope)
  }

  /**
   * 渲染筛选条件的计数显示
   * 更新筛选器摘要、标题、包装元素等
   *
   * @param {HTMLElement} source - 源元素（来自服务器响应）
   * @param {HTMLElement} target - 目标元素（当前 DOM 中的元素）
   */
  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary')
    const sourceSummary = source.querySelector('.facets__summary')
    if (sourceSummary && targetSummary) targetSummary.outerHTML = sourceSummary.outerHTML

    const targetHeaderElement = target.querySelector('.facets__header')
    const sourceHeaderElement = source.querySelector('.facets__header')
    if (sourceHeaderElement && targetHeaderElement) targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML

    const targetWrapElement = target.querySelector('.facets-wrap')
    const sourceWrapElement = source.querySelector('.facets-wrap')

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'))
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach(hiddenItem => hiddenItem.classList.replace('hidden', 'show-more-item'))
      }
      // 如果当前显示"更多"状态，保留隐藏项的 show-more-item 类
      targetWrapElement.outerHTML = sourceWrapElement.outerHTML
    }
  }

  /**
   * 渲染移动端筛选条件的计数显示
   *
   * @param {HTMLElement} source - 源元素（来自服务器响应）
   * @param {HTMLElement} target - 目标元素（当前 DOM 中的元素）
   */
  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list-js')
    const sourceFacetsList = source.querySelector('.mobile-facets__list-js')
    if (sourceFacetsList && targetFacetsList) targetFacetsList.outerHTML = sourceFacetsList.outerHTML
  }

  /**
   * 更新浏览器 URL（使用 pushState，不刷新页面）
   *
   * @param {string} searchParams - URL 搜索参数字符串
   */
  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`)
  }

  /**
   * 获取需要更新的 section ID
   * 用于构建 AJAX 请求 URL
   *
   * @returns {Array} section 配置数组
   */
  static getSections() {
    return [{ section: document.getElementById('product-grid')?.dataset?.id }].filter(s => s.section)
  }

  /**
   * 从表单创建搜索参数字符串
   *
   * @param {HTMLFormElement} form - 表单元素
   * @returns {string} URL 搜索参数字符串
   */
  createSearchParams(form) {
    const formData = new FormData(form)
    return new URLSearchParams(formData).toString()
  }

  /**
   * 提交表单并渲染页面
   *
   * @param {string} searchParams - URL 搜索参数字符串
   * @param {Event} event - 触发提交的事件对象
   */
  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event)
  }

  /**
   * 表单提交处理器
   * 根据不同的表单类型（桌面端/移动端）处理提交逻辑
   *
   * @param {Event} event - 提交事件对象
   */
  onSubmitHandler(event) {
    event.preventDefault()

    const form = event.target.closest('form')
    if (!form) return

    // 基于当前 URL 的查询参数，叠加本次表单的变更，避免排序和筛选互相覆盖
    const baseParams = new URLSearchParams(window.location.search.slice(1))
    const formParams = new URLSearchParams(new FormData(form))

    // 获取表单中所有 checkbox 的 name 属性（筛选参数）
    const filterParamNames = new Set()
    form.querySelectorAll('input[type="checkbox"]').forEach(input => {
      if (input.name && input.name.startsWith('filter.')) {
        filterParamNames.add(input.name)
      }
    })

    // 对于筛选参数：如果表单中没有（checkbox 未选中），则从 baseParams 中删除
    filterParamNames.forEach(paramName => {
      if (!formParams.has(paramName)) {
        // 删除所有同名参数（可能有多个值）
        baseParams.delete(paramName)
      }
    })

    // 先移除 base 中同名参数，再用表单里的值覆盖
    formParams.forEach((_value, key) => {
      baseParams.delete(key)
    })
    formParams.forEach((value, key) => {
      baseParams.append(key, value)
    })

    this.onSubmitForm(baseParams.toString(), event)
  }

  /**
   * 活动筛选条件点击处理器
   * 当用户点击已选中的筛选条件标签（移除按钮）时调用
   *
   * @param {Event} event - 点击事件对象
   */
  onActiveFilterClick(event) {
    event.preventDefault()

    // 使用可选链安全地访问 href
    const href = event.currentTarget?.href || ''
    const url = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1)

    FacetFiltersForm.renderPage(url)
  }
}

/**
 * =========================================================
 * PriceRange - 价格范围筛选自定义元素类
 * =========================================================
 *
 * 功能说明：
 * 处理价格范围输入框的验证和交互
 * 确保输入的价格在有效范围内
 */
class PriceRange extends HTMLElement {
  constructor() {
    super()
    // 为所有价格输入框绑定事件
    this.querySelectorAll('input').forEach(el => {
      el.addEventListener('change', this.onRangeChange.bind(this))
      el.addEventListener('keydown', this.onKeyDown.bind(this))
    })
    // 初始化最小值和最大值的限制
    this.setMinAndMaxValues()
  }

  /**
   * 价格范围输入变化处理器
   * 当用户修改价格输入时，验证并调整值
   *
   * @param {Event} event - change 事件对象
   */
  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget)
    this.setMinAndMaxValues()
  }

  /**
   * 键盘输入处理器
   * 限制只能输入数字、小数点、逗号等有效字符
   *
   * @param {Event} event - keydown 事件对象
   */
  onKeyDown(event) {
    if (event.metaKey) return
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/
    if (!event.key.match(pattern)) event.preventDefault()
  }

  /**
   * 设置最小值和最大值的限制
   * 通过 data-min 和 data-max 属性实现输入框之间的相互约束
   */
  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input')
    const minInput = inputs[0]
    const maxInput = inputs[1]
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value)
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value)
    if (minInput.value === '') maxInput.setAttribute('data-min', 0)
    if (maxInput.value === '') minInput.setAttribute('data-max', minInput.getAttribute('data-max'))
  }

  /**
   * 调整输入值到有效范围
   * 如果输入的值超出限制，自动修正为边界值
   *
   * @param {HTMLInputElement} input - 输入框元素
   */
  adjustToValidValues(input) {
    const value = Number(input.value)
    const min = Number(input.getAttribute('data-min'))
    const max = Number(input.getAttribute('data-max'))
    if (value < min) input.value = min
    if (value > max) input.value = max
  }
}

/**
 * =========================================================
 * FacetRemove - 移除筛选条件自定义元素类
 * =========================================================
 *
 * 功能说明：
 * 处理已选中筛选条件标签的移除功能
 * 支持鼠标点击和键盘空格键操作
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super()
    const facetLink = this.querySelector('a')
    if (!facetLink) return

    // 设置角色为按钮，提升可访问性
    facetLink.setAttribute('role', 'button')
    // 绑定点击事件
    facetLink.addEventListener('click', this.closeFilter.bind(this))
    // 绑定键盘事件（空格键）
    facetLink.addEventListener('keyup', event => {
      event.preventDefault()
      // 使用可选链安全地访问 code
      if (event.code?.toUpperCase() === 'SPACE') this.closeFilter(event)
    })
  }

  /**
   * 关闭/移除筛选条件
   * 触发筛选表单的提交，移除当前筛选条件
   *
   * @param {Event} event - 触发事件对象
   */
  closeFilter(event) {
    event.preventDefault()
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form')
    if (form) form.onActiveFilterClick(event)
  }
}

// =========================================================
// 注册自定义元素（防止重复注册）
// =========================================================
if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm)
}
if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange)
}
if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove)
}

// =========================================================
// 模块初始化：页面加载时执行
// =========================================================

// 初始化静态属性
FacetFiltersForm.filterData = [] // AJAX 响应缓存
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1) // 初始 URL 参数
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1) // 上一次的 URL 参数
FacetFiltersForm.currentGridLayout = null // 当前网格布局状态（由 getCurrentGridLayout() 从 sessionStorage 恢复）

// 设置浏览器历史记录监听
FacetFiltersForm.setListeners()

// 设置筛选条件计数功能的初始化
FacetFiltersForm.setupActiveFacetCountInit()

// 设置 DOM 变化监听（自动更新计数和重新初始化排序）
FacetFiltersForm.setupActiveFacetCountObserver()

// 首屏初始化自定义排序下拉组件
// 使用 IIFE 包裹，分号防止与前一行代码粘连
;(() => {
  initComponentFacetsSort()
})()

/**
 * 集合页筛选/排序条：滚动吸顶（哨兵 + 占位 + IntersectionObserver）
 * - 不修改 body.paddingTop，避免 reflow 抖动
 * - 占位元素吸顶时撑住原位高度，避免内容跳动
 */
;(() => {
  const bar = document.querySelector('.component-facets__filters-sort-container')
  if (!bar) return

  const FIXED_CLASS = 'component-facets__filters-sort--fixed'

  if (window.__FACETS_SCROLL_HANDLER__) {
    window.removeEventListener('scroll', window.__FACETS_SCROLL_HANDLER__)
    window.__FACETS_SCROLL_HANDLER__ = null
  }
  if (window.__FACETS_OBSERVER__) {
    window.__FACETS_OBSERVER__.disconnect()
    window.__FACETS_OBSERVER__ = null
  }
  if (window.__FACETS_RESIZE_HANDLER__) {
    window.removeEventListener('resize', window.__FACETS_RESIZE_HANDLER__)
    window.__FACETS_RESIZE_HANDLER__ = null
  }
  document.body.style.paddingTop = ''
  bar.style.top = ''
  bar.classList.remove(FIXED_CLASS)

  let placeholder = bar.previousElementSibling
  if (!placeholder || !placeholder.classList || !placeholder.classList.contains('component-facets__filters-sort-placeholder')) {
    placeholder = document.createElement('div')
    placeholder.className = 'component-facets__filters-sort-placeholder'
    placeholder.style.cssText = 'width:100%;height:0;'
    bar.parentNode.insertBefore(placeholder, bar)
  } else {
    placeholder.style.height = '0px'
  }

  let sentinel = placeholder.previousElementSibling
  if (!sentinel || !sentinel.classList || !sentinel.classList.contains('component-facets__filters-sort-sentinel')) {
    sentinel = document.createElement('div')
    sentinel.className = 'component-facets__filters-sort-sentinel'
    sentinel.style.cssText = 'width:100%;height:1px;pointer-events:none;'
    bar.parentNode.insertBefore(sentinel, placeholder)
  }

  let naturalHeight = bar.offsetHeight

  const setFixed = (fixed) => {
    const isFixed = bar.classList.contains(FIXED_CLASS)
    if (fixed === isFixed) return
    if (fixed) {
      naturalHeight = bar.offsetHeight
      bar.classList.add(FIXED_CLASS)
      placeholder.style.height = naturalHeight + 'px'
    } else {
      bar.classList.remove(FIXED_CLASS)
      placeholder.style.height = '0px'
    }
  }

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0]
      setFixed(!entry.isIntersecting && entry.boundingClientRect.top <= 0)
    },
    { threshold: 0 }
  )
  observer.observe(sentinel)
  window.__FACETS_OBSERVER__ = observer

  const onResize = () => {
    if (bar.classList.contains(FIXED_CLASS)) {
      bar.classList.remove(FIXED_CLASS)
      placeholder.style.height = '0px'
      naturalHeight = bar.offsetHeight
      bar.classList.add(FIXED_CLASS)
      placeholder.style.height = naturalHeight + 'px'
    } else {
      naturalHeight = bar.offsetHeight
    }
  }
  window.__FACETS_RESIZE_HANDLER__ = onResize
  window.addEventListener('resize', onResize)
})()
/**
 * 网格视图切换功能
 * 支持筛选/排序后保持用户选择的视图状态
 * 状态存储在 FacetFiltersForm 类的静态属性中，并通过 sessionStorage 持久化
 */
;(function () {
  // 初始化：从 sessionStorage 恢复或从当前网格容器读取默认状态
  let initialLayout = sessionStorage.getItem('shopify-grid-layout')

  if (!initialLayout) {
    // 如果 sessionStorage 中没有保存，则从 DOM 读取
    const productGrid = document.querySelector('.wd-collection__grid')
    if (productGrid) {
      if (productGrid.classList.contains('grid-layout-2')) {
        initialLayout = 'grid-layout-2'
      } else {
        initialLayout = 'grid-layout-1'
      }
    } else {
      initialLayout = 'grid-layout-1'
    }
  }

  // 设置初始状态到类静态属性
  FacetFiltersForm.setCurrentGridLayout(initialLayout)

  // 绑定切换按钮事件（使用事件委托，支持动态加载的按钮）
  document.addEventListener('click', e => {
    const btn = e.target.closest('.wd-grid-toggle__btn')
    if (!btn) return

    const gridType = btn.getAttribute('data-grid-type')
    if (!gridType) return

    const currentProductGrid = document.querySelector('.wd-collection__grid')
    if (!currentProductGrid) return

    // 保存当前选择的网格布局（使用类静态方法，自动持久化）
    const newLayout = `grid-layout-${gridType.split('-')[1]}`
    FacetFiltersForm.setCurrentGridLayout(newLayout)

    // 更新所有按钮的激活状态和 aria-pressed 属性
    const toggleBtns = document.querySelectorAll('.wd-grid-toggle__btn')
    toggleBtns.forEach(b => {
      const isActive = b === btn
      b.classList.toggle('active', isActive)
      b.setAttribute('aria-pressed', isActive ? 'true' : 'false')
    })

    // 移除所有网格类型并添加新的
    currentProductGrid.classList.remove('grid-layout-1', 'grid-layout-2')
    currentProductGrid.classList.add(newLayout)
  })
})()

/**
 * Clear All 按钮功能增强
 * 拦截 .component-facets__clear-js 按钮的点击，使用 AJAX 而非页面跳转
 * 确保清除筛选时保持网格视图状态
 */
;(function () {
  document.addEventListener('click', e => {
    const clearBtn = e.target.closest('.component-facets__clear-js')
    if (!clearBtn) return

    e.preventDefault()

    // 从链接中提取 URL 参数（Clear All 按钮通常指向原始 URL，没有筛选参数）
    const href = clearBtn.getAttribute('href') || ''
    const url = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1)

    // 使用 renderPage 方法清除筛选（会自动恢复网格状态）
    if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
      FacetFiltersForm.renderPage(url)
    }
  })
})()
